import { validInt } from 'booksprout'
import { BaseModel } from '../modules/_base/base.model'

/**
 * Loading the one document a view or edit screen shows.
 *
 * The screen either loads by id from the url, or is handed a model it should display instead — that is
 * what `assignModel` is for, and it is how sub-forms work: the review-sites panel inside an arc's
 * create/edit screen is the same component, given the arc rather than a url to read.
 *
 * The model itself stays with the caller (`assign`), because every page owns its own `crudModel` field
 * and its own initial value.
 *
 * **Nothing here may call a method the class can override.** `doOnCreate` and `reload` stay on the mixin
 * for that reason: pages override `getDocId` (BlockMember prefers a prop over the route param),
 * `loadDoc` (the settings screens ignore the id and always ask for the authenticated user) and
 * `assignLoadedDoc`, and a composable calling its own internal copy would skip every one of those
 * silently. Anything the class can replace arrives through the context — `getByIdArgs`, `onLoadDoc`,
 * `assignLoadedDoc` — so this file only ever calls what it was handed. The constraint goes away in
 * stage 8, where a component overrides by passing a different context rather than by subclassing.
 */

export interface ViewModelContext<TModel extends BaseModel> {
  crudService: { getById (id?: number, args?: any): Promise<TModel | null> }
  route: { params: { id?: string } }
  /** What the caller wants to store when a document arrives. */
  assign: (doc: TModel) => void
  /** Extra args for the read — a page overrides this to ask for related records. */
  getByIdArgs?: () => any
  /** Called after a document has been assigned, for a page to derive its own state from it. */
  onLoadDoc?: () => void
  /**
   * How to store a document that has arrived. The mixin passes its own overridable method, so a page
   * that overrides `assignLoadedDoc` is still the one that runs. Nothing on the mixin chain does today —
   * the settings screens did, and they are `useSettingsCrud` now — but relying on that would be luck
   * rather than design.
   */
  assignLoadedDoc?: (doc: TModel | null) => void
  onError: (message: string) => void
}

export function useViewModel<TModel extends BaseModel> (context: ViewModelContext<TModel>) {
  const getDocId = (): number => {
    return parseInt(context.route.params.id as string)
  }

  const assignLoadedDoc = (doc: TModel | null) => {
    if (doc !== null) {
      context.assign(doc)
      context.onLoadDoc?.()
    }
  }

  /**
   * Silently does nothing for a route with no numeric id — a create screen is the same component as an
   * edit screen, and it arrives without one.
   */
  const loadDoc = (docId?: number) => {
    if (!validInt(docId)) {
      return
    }

    const assign = context.assignLoadedDoc ?? assignLoadedDoc

    return context.crudService.getById(docId, context.getByIdArgs?.()).then((doc: TModel | null) => {
      assign(doc)
    }).catch(e => {
      context.onError(e.message)
    })
  }

  return {
    getDocId,
    loadDoc,
    assignLoadedDoc
  }
}
